import { useEffect, useRef } from 'react';
import { loadBillingStatus, refreshAccessAfterPayment } from './cloud';

const POLL_INTERVAL_MS = 2000;
const POLL_ATTEMPTS = 10;

/**
 * Håndterer brugeren, når Stripe sender dem tilbage (`?billing=success|cancel|portal`).
 * Adgangen gives af Stripe-webhooken, ikke af denne side, så vi venter på, at serveren har
 * registreret betalingen, henter nyt token og genindlæser, så det rigtige niveau vises.
 */
export function useBillingReturn(
  signedIn: boolean,
  notify: (message: string) => void,
  refreshAccountAccess: () => Promise<void>,
) {
  const handled = useRef(false);
  const outcomeRef = useRef<string | null>(null);
  // `notify` får ny identitet ved hver render. Via ref afbrydes pollingen ikke af gen-renders.
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const refreshRef = useRef(refreshAccountAccess);
  refreshRef.current = refreshAccountAccess;

  useEffect(() => {
    const notify = (message: string) => notifyRef.current(message);
    if (!signedIn || handled.current) return;
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('billing') ?? outcomeRef.current;
    if (!outcome || !['success', 'cancel', 'portal'].includes(outcome)) return;
    outcomeRef.current = outcome;
    handled.current = true;
    params.delete('billing');
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
    );

    if (outcome === 'cancel') {
      notify('Betalingsforløbet blev afbrudt. Du kan kontrollere dit abonnement under Konto.');
      return;
    }

    notify(
      outcome === 'portal'
        ? 'Vi kontrollerer dit abonnement efter besøget hos Stripe…'
        : 'Vi kontrollerer betalingen og aktiverer dit abonnement…',
    );
    let cancelled = false;
    let finished = false;
    (async () => {
      let lastSubscription: string | undefined;
      let accessRefreshed = false;
      for (let attempt = 0; attempt < POLL_ATTEMPTS && !cancelled; attempt += 1) {
        try {
          const status = await loadBillingStatus();
          if (cancelled) return;
          const subscriptionKey = JSON.stringify(status.subscription);
          if (
            (outcome === 'portal' ||
              (status.subscription && status.subscription.tier !== 'free')) &&
            subscriptionKey !== lastSubscription
          ) {
            await refreshAccessAfterPayment();
            if (cancelled) return;
            await refreshRef.current();
            accessRefreshed = true;
            lastSubscription = subscriptionKey;
            if (outcome === 'success') {
              finished = true;
              notify('Dit abonnement er aktivt.');
              return;
            }
          }
        } catch {
          // En forsinket webhook eller en afbrudt forbindelse må ikke give falsk bekræftelse.
        }
        if (attempt < POLL_ATTEMPTS - 1)
          await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }
      if (!cancelled) {
        finished = true;
        notify(
          outcome === 'portal' && accessRefreshed
            ? 'Din abonnementsstatus er hentet. Du kan se detaljerne under Konto.'
            : 'Aktiveringen kunne ikke bekræftes endnu. Kontrollér abonnementet under Konto, eller prøv igen om lidt.',
        );
      }
    })();
    return () => {
      cancelled = true;
      if (!finished) handled.current = false;
    };
  }, [signedIn]);
}
